/**
 * Client-side file downloads.
 *
 * SIMULATED CONTENT. Everything produced here is a text file. There is no audio
 * encoder, no stem separator, and no asset server, so a "WAV download" writes a
 * .txt placeholder that says so. The filenames and toast copy were corrected in
 * Phase 1 to stop claiming to deliver audio the app does not have.
 *
 * The Blob mechanism itself is real and stays as-is; only the *contents* change
 * once a backend can serve signed URLs for actual files — which is what
 * `downloadFromUrl` below is for. See docs/api-contract.md §30 for both halves.
 */
import { escapeHtml } from './escapeHtml';
import { joinWithMiddleDot } from './formatting';
import { buildPrintDocument } from './printBrand';
import type { Song, VaultPack } from '../types/domain';

const BLOB_URL_CLEANUP_DELAY_MS = 1_000;

function triggerBlobDownload(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Firefox and Safari can still be materializing a large download after click() returns.
  // Revoking synchronously can leave a valid HTML shell whose embedded data images are lost.
  window.setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_CLEANUP_DELAY_MS);
}

/** Triggers a browser download of an in-memory string. */
export function downloadTextFile(filename: string, contents: string, mime = 'text/plain'): void {
  triggerBlobDownload(filename, new Blob([contents], { type: mime }));
}

/** Triggers a browser download of an in-memory Blob. */
export function downloadBlobFile(filename: string, blob: Blob): void {
  triggerBlobDownload(filename, blob);
}

/** Decodes a base64 payload into the bytes a Blob expects. */
function decodeBase64(dataBase64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(dataBase64);
  const bytes = new Uint8Array(binary.length);
  for (let index = 0; index < binary.length; index += 1) {
    bytes[index] = binary.charCodeAt(index);
  }
  return bytes;
}

/** Triggers a browser download of an in-memory binary file. */
export function downloadBase64File(filename: string, dataBase64: string, mimeType: string): void {
  const bytes = decodeBase64(dataBase64);
  const blob = new Blob([bytes], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => URL.revokeObjectURL(url), BLOB_URL_CLEANUP_DELAY_MS);
}

/** Turns a title into a filename-safe stem. */
function slug(title: string): string {
  return title.replace(/\s+/g, '_');
}

/**
 * Saves a file the SERVER produced, from its URL.
 *
 * ==========================================================================
 * THE RECEIVING HALF OF THE DOWNLOAD BOUNDARY (Batch 7 §10), and the one function in this
 * file that writes real audio — on the day there is any. It invents nothing: it is handed a
 * URL by `api/audioEditApi.exportAudio` and asks the browser to save it.
 *
 * NOTHING CALLS THIS WITH A REAL URL TODAY. `audioEditApi.supports('exportAudio')` is
 * false, so the download paths in hooks/useSongs and hooks/useStudio take their
 * placeholder branch and this is reached only by the test that spies the capability on.
 * That is deliberate: §10 asks for a clean boundary for receiving an audio or stem
 * download URL, and a boundary nothing can call is an intention rather than a seam.
 * ==========================================================================
 *
 * Works for a hosted URL, a signed URL and a blob URL alike, which is why the parameter is
 * a string rather than a `Blob`: which of the three a backend hands over is its choice
 * (`vaultApi.recordPackDownload`'s note says the real endpoint returns a signed URL), and
 * the caller that has a Blob can `URL.createObjectURL` it in one line. No `revokeObjectURL`
 * here for that reason — this function did not create the URL and must not invalidate one
 * its caller may still need.
 *
 * `download` is honoured only for same-origin URLs and is a hint elsewhere; a
 * cross-origin asset host will name the file from its own headers instead. That is the
 * server's job to get right (`Content-Disposition`), and the attribute is set anyway
 * because it costs nothing and is correct when the asset is served from the app's origin.
 */
export function downloadFromUrl(url: string, filename?: string): void {
  const a = document.createElement('a');
  a.href = url;
  if (filename) a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/**
 * Starts a browser-managed audio download from a real URL.
 *
 * Deliberately does NOT fetch the file into a Blob first. Handing the export URL straight to
 * the browser means the save prompt, progress UI, and pause/resume all belong to the browser's
 * own download manager rather than to a finished in-memory Blob the app already fetched.
 *
 * The filename is left to the server's `Content-Disposition` header rather than being forced by
 * the anchor's `download` attribute. That gives the browser its normal download path, including
 * asking for a save location first when the user's browser settings are configured that way.
 */
export function downloadAudioFromUrl(url: string, _title: string, _format: 'mp3' | 'wav'): void {
  downloadFromUrl(url);
}

/**
 * Saves a song's uploaded cover image.
 *
 * REAL, unlike everything else in this file. An uploaded cover is the user's own
 * image held as a data URL, so there is an actual file to write — no placeholder and
 * no apology. Generated artwork is a gradient computed at render time with no bytes
 * behind it, which is why the caller checks `coverUrl` first and falls back to a
 * message rather than this function.
 *
 * The extension is read off the data URL's MIME type instead of assumed, so a PNG
 * does not get saved as `.jpg`.
 */
export function downloadCoverImage(song: Song, dataUrl: string): string {
  const mime = /^data:([^;,]+)/.exec(dataUrl)?.[1] ?? 'image/png';
  const ext = mime.split('/')[1]?.replace('jpeg', 'jpg') ?? 'png';
  const filename = `${slug(song.title)}_cover.${ext}`;

  // An <a download> pointed at the data URL, rather than a Blob: the bytes are
  // already encoded in the string, and decoding them only to re-encode a Blob would
  // double the memory for no gain.
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  return filename;
}

/**
 * Builds the text details file offered in place of an audio export.
 *
 * `requestedFormat` records what the user actually asked for ("MP3"/"WAV") so
 * the file and toast can be honest about the substitution rather than silently
 * handing over a .txt named like a track.
 *
 * Footed with the wordmark to match the lyric file. Both already led with "Lyrixa AI";
 * a plain-text file cannot carry the mark, so that is as far as branding goes here — the
 * printed sheet is where the real one appears (lib/printBrand.ts).
 */
export function buildSongDetailsFile(
  song: Song,
  requestedFormat?: string
): { filename: string; contents: string } {
  const header = requestedFormat
    ? `Lyrixsa AI — ${requestedFormat} export placeholder\n\nAudio export is not available yet. This file contains the song's details only.`
    : `Lyrixsa AI — song details`;
  const contents = [
    header,
    ``,
    `Title: ${song.title}`,
    ...(song.genre ? [`Genre: ${song.genre}`] : []),
    `Length: ${song.length}`,
    `Created: ${song.date}`,
    ``,
    `----------------------------------------`,
    `Created with Lyrixsa AI · Lyrixa.ai`,
    ``,
  ].join('\n');
  const suffix = requestedFormat ? `_${requestedFormat}_details` : '_details';
  return { filename: `${slug(song.title)}${suffix}.txt`, contents };
}

/** Builds the placeholder file for a single separated stem. */
export function buildStemFile(
  song: Song,
  stemName: string
): { filename: string; contents: string } {
  return {
    filename: `${slug(song.title)}_${stemName}.wav.txt`,
    contents: `Lyrixsa AI — simulated stem file\nSong: ${song.title}\nStem: ${stemName}\n`,
  };
}

const STORED_STEM_MANIFEST_LABELS = ['Vocals', 'Bass', 'Drums', 'Guitar', 'Piano', 'Other'] as const;

/**
 * Builds the manifest that stands in for a stems ZIP.
 *
 * The placeholder names the same stored stem files the backend can really serve, so the
 * fixed list here matches the six Demucs stems Studio exposes everywhere else.
 */
export function buildStemsManifest(song: Song): { filename: string; contents: string } {
  const includes = STORED_STEM_MANIFEST_LABELS.map((label) => `${label}.wav`).join(', ');
  return {
    filename: `${slug(song.title)}_stems_manifest.txt`,
    contents: `Lyrixsa AI — stem package (simulated)\nSong: ${song.title}\nIncludes: ${includes}\n`,
  };
}

/**
 * Builds the lyrics text file for a song.
 *
 * Headed and footed like the details file, which it previously was not — it wrote out
 * `song.lyrics` bare, so a downloaded sheet arrived with no title, no attribution and
 * nothing to say where it came from. A plain-text file cannot carry the mark, so the
 * wordmark stands in; the printed sheet gets the real one (lib/printBrand.ts).
 */
export function buildLyricsFile(song: Song): { filename: string; contents: string } {
  const contents = [
    `Lyrixsa AI — lyric sheet`,
    ``,
    song.title,
    joinWithMiddleDot([song.genre, song.length, song.date]),
    ``,
    `----------------------------------------`,
    ``,
    song.lyrics || '(no lyrics)',
    ``,
    `----------------------------------------`,
    `Created with Lyrixsa AI · Lyrixa.ai`,
    ``,
  ].join('\n');
  return { filename: `${slug(song.title)}_lyrics.txt`, contents };
}

/**
 * Builds the printable lyric sheet — the branded version of what the Print button
 * used to emit, which was a single `<pre>` with the title on the first line.
 *
 * The metadata row is genre, length and date and nothing else. A printed sheet is
 * something someone holds while they sing or hands to a collaborator, so the prompt,
 * the credit cost and the version history are deliberately absent — the same narrow
 * projection the public share page takes, and for the same reason.
 *
 * `escapeHtml` before it reaches `bodyHtml`, because buildPrintDocument escapes every
 * field EXCEPT that one; see its header. Lyrics keep their newlines rather than being
 * turned into `<br />`, because the sheet's CSS is `white-space: pre-wrap` and the
 * blank lines between verses are the structure.
 */
export function buildLyricsPrintDocument(song: Song): string {
  return buildPrintDocument({
    documentTitle: `${song.title} — lyrics · Lyrixsa AI`,
    kind: 'Lyric sheet',
    heading: song.title,
    subheading: joinWithMiddleDot([song.genre, song.length, song.date]),
    bodyHtml: `<pre class="lyx-print-lyrics">${escapeHtml(song.lyrics || '')}</pre>`,
  });
}

/**
 * Builds the full text manifest for a Vault pack.
 *
 * This one is genuinely useful output rather than a placeholder — the pack's
 * value is its written prompts, presets and copy, all of which are included.
 */
export function buildVaultPackManifest(pack: VaultPack): { filename: string; contents: string } {
  const contents = [
    `Lyrixsa AI — DFY Producer Vault`,
    `Pack: ${pack.name}`,
    `Version: ${pack.version}`,
    `Includes: ${pack.prompts.length} prompts, ${pack.presets.length} presets, ${pack.structures.length} structures, ${pack.hooks.length} hooks, ${pack.metadata.length} metadata templates, ${pack.marketing.length} marketing assets`,
    ``,
    ...pack.prompts.map((p) => `[Prompt] ${p.title}: ${p.text}`),
    ...pack.presets.map(
      (p) =>
        `[Preset] ${p.name}: ${p.tempo} tempo, ${p.mood} mood, ${p.energy}% energy, ${p.vocals}, structure: ${p.structure}`
    ),
    ...pack.structures.map((s) => `[Structure] ${s.name}: ${s.layout}`),
    ...pack.hooks.map((h) => `[Hook] ${h.text}`),
    ...pack.metadata.map((m) => `[Metadata · ${m.platform}] ${m.title}: ${m.text}`),
    ...pack.marketing.map((m) => `[Marketing · ${m.platform}] ${m.title}: ${m.text}`),
  ].join('\n');
  return { filename: `${slug(pack.name)}_v${pack.version}.txt`, contents };
}
